/**
 * Position Reconciliation — phase 5 of broker integration.
 *
 * Compares NigaStock's local positions view (fed by WS FillEmitted events)
 * against each linked broker's reported positions (fetched by the broker
 * poller every 5 minutes). Emits a diff with severity classifications:
 *
 *   match      — qty + avg price agree within tolerance (no action)
 *   drift_qty  — quantity differs; most common cause is a stale cache or
 *                a missed FillEmitted; usually resolves on next poll
 *   drift_px   — qty matches but broker avg price differs from local
 *   missing_local   — broker reports a position NigaStock doesn't know about
 *   missing_broker  — local thinks we have a position; broker doesn't
 *
 * Resolutions:
 *   accept_broker  — update local to match broker (writes to local cache +
 *                    emits a `ReconResolution` event the backend consumes)
 *   investigate    — creates a ticket in the support queue; blocks trading
 *                    on that symbol until an operator clears it
 *   dismiss        — marks the diff as reviewed (informational only)
 *
 * All reconciliation outputs land in the same S3 Object-Lock audit bucket
 * the rest of the trading stack writes to, with a 7-year retention tag.
 */

import { BROKERS, type BrokerId } from './brokers';

export interface LocalPosition {
  brokerId: BrokerId;
  symbol: string;
  quantity: number;
  averagePriceKobo: string;
}
export interface BrokerPosition {
  brokerId: BrokerId;
  symbol: string;
  quantity: number;
  averagePriceKobo: string;
}

export type ReconKind = 'match' | 'drift_qty' | 'drift_px' | 'missing_local' | 'missing_broker';
export type Severity = 'ok' | 'info' | 'warn' | 'error';

export interface ReconFinding {
  brokerId: BrokerId;
  symbol: string;
  kind: ReconKind;
  severity: Severity;
  localQty?: number;
  brokerQty?: number;
  localAvgKobo?: string;
  brokerAvgKobo?: string;
  qtyDelta?: number;
  pxDeltaKobo?: string;
  message: string;
}

/**
 * Default tolerance: 0n kobo for quantity (integer), 50 kobo for avg price
 * (half a naira — accounts for weighted-average rounding across fills).
 */
export interface ReconOpts {
  pxToleranceKobo?: bigint;
}

export function reconcile(
  local: LocalPosition[],
  broker: BrokerPosition[],
  opts: ReconOpts = {},
): ReconFinding[] {
  const pxTol = opts.pxToleranceKobo ?? 50n;
  const findings: ReconFinding[] = [];
  const seen = new Set<string>();

  const key = (p: { brokerId: BrokerId; symbol: string }) => p.brokerId + '::' + p.symbol;

  for (const b of broker) {
    const k = key(b);
    seen.add(k);
    const l = local.find((x) => key(x) === k);
    if (!l) {
      findings.push({
        brokerId: b.brokerId, symbol: b.symbol,
        kind: 'missing_local', severity: 'warn',
        brokerQty: b.quantity, brokerAvgKobo: b.averagePriceKobo,
        message: `Broker reports ${b.quantity} units of ${b.symbol} not in local cache.`,
      });
      continue;
    }
    if (l.quantity !== b.quantity) {
      findings.push({
        brokerId: b.brokerId, symbol: b.symbol,
        kind: 'drift_qty', severity: 'error',
        localQty: l.quantity, brokerQty: b.quantity,
        qtyDelta: b.quantity - l.quantity,
        message: `Quantity drift on ${b.symbol}: local ${l.quantity} vs broker ${b.quantity}.`,
      });
      continue;
    }
    const lpx = BigInt(l.averagePriceKobo), bpx = BigInt(b.averagePriceKobo);
    const delta = bpx > lpx ? bpx - lpx : lpx - bpx;
    if (delta > pxTol) {
      findings.push({
        brokerId: b.brokerId, symbol: b.symbol,
        kind: 'drift_px', severity: 'info',
        localQty: l.quantity, brokerQty: b.quantity,
        localAvgKobo: l.averagePriceKobo, brokerAvgKobo: b.averagePriceKobo,
        pxDeltaKobo: delta.toString(),
        message: `Avg price drift on ${b.symbol}: local ${l.averagePriceKobo} vs broker ${b.averagePriceKobo}.`,
      });
      continue;
    }
    findings.push({
      brokerId: b.brokerId, symbol: b.symbol,
      kind: 'match', severity: 'ok',
      localQty: l.quantity, brokerQty: b.quantity,
      message: `${b.symbol} reconciled cleanly (${b.quantity} units).`,
    });
  }

  // Positions present locally but not on any broker report
  for (const l of local) {
    if (!seen.has(key(l))) {
      findings.push({
        brokerId: l.brokerId, symbol: l.symbol,
        kind: 'missing_broker', severity: 'error',
        localQty: l.quantity, localAvgKobo: l.averagePriceKobo,
        message: `Local cache shows ${l.quantity} units of ${l.symbol} but broker returned no position.`,
      });
    }
  }

  return findings;
}

/** Roll up by severity for the UI summary pills. */
export function summarise(findings: ReconFinding[]) {
  const s = { ok: 0, info: 0, warn: 0, error: 0 };
  for (const f of findings) s[f.severity]++;
  return s;
}

/**
 * Seeded demo inputs so the UI has realistic data without a backend. The
 * tuple simulates what the reconciliation agent would have produced on the
 * most recent 5-minute tick. In prod both arrays come from BAL endpoints.
 */
export function seedDemoRecon() {
  const local: LocalPosition[] = [
    { brokerId: 'bamboo', symbol: 'ZENITHBANK', quantity: 1000, averagePriceKobo: '425000' },
    { brokerId: 'bamboo', symbol: 'DANGCEM',    quantity: 50,   averagePriceKobo: '6825000' },
    { brokerId: 'bamboo', symbol: 'MTNN',       quantity: 200,  averagePriceKobo: '2380000' },
    { brokerId: 'chd',    symbol: 'FGN29-13.53', quantity: 5000, averagePriceKobo: '94200000' },
    { brokerId: 'meristem', symbol: 'NEWGOLD',  quantity: 2,    averagePriceKobo: '5820000' },
  ];
  const broker: BrokerPosition[] = [
    { brokerId: 'bamboo', symbol: 'ZENITHBANK', quantity: 1000, averagePriceKobo: '425000' },
    { brokerId: 'bamboo', symbol: 'DANGCEM',    quantity: 50,   averagePriceKobo: '6824200' }, // price drift (800 kobo)
    { brokerId: 'bamboo', symbol: 'MTNN',       quantity: 250,  averagePriceKobo: '2380000' }, // qty drift (+50)
    { brokerId: 'chd',    symbol: 'FGN29-13.53', quantity: 5000, averagePriceKobo: '94200000' },
    { brokerId: 'meristem', symbol: 'NEWGOLD',  quantity: 2,    averagePriceKobo: '5820000' },
    { brokerId: 'meristem', symbol: 'LOTUSHAL15', quantity: 1000, averagePriceKobo: '1425' },    // missing_local
  ];
  return { local, broker };
}
